import { App } from "antd";
import { FileTextOutlined, CheckCircleFilled, FileDoneOutlined, FileSearchOutlined, FileAddOutlined } from "@ant-design/icons";
import type { FormData, ChooseProduct } from "@ifa/utils";
import { downloadOrderFormPdf } from "@ifa/utils";
import { useMemo, useState } from "react";
import CustomButton from "../../../../../../components/CustomButton";
import { resolveFnaNeedsAnalysisDisplay } from "../../../../../order-fna/resolveFnaNeedsAnalysisDisplay.js";
import styles from "./FnaNeedsAnalysis.module.css";

interface FnaNeedsAnalysisProps {
  financialAnalysis?: FormData[];
  chooseProducts?: ChooseProduct[];
  sourceOrderId?: number;
  isZuuFinancialStepMode?: boolean;
}

export function FnaNeedsAnalysis({
  financialAnalysis,
  chooseProducts,
  sourceOrderId,
  isZuuFinancialStepMode = false,
}: FnaNeedsAnalysisProps) {
  const { message } = App.useApp();
  const [previewing, setPreviewing] = useState(false);
  const { targetTags, paymentPeriod, coveragePeriod } = useMemo(
    () =>
      resolveFnaNeedsAnalysisDisplay(financialAnalysis, {
        isZuuMode: isZuuFinancialStepMode,
      }),
    [financialAnalysis, isZuuFinancialStepMode],
  );

  // 介绍的产品：所有产品
  const introducedProducts = useMemo(() => {
    return chooseProducts || [];
  }, [chooseProducts]);

  // 选择的产品：choose === true 的产品
  const selectedProducts = useMemo(() => {
    return chooseProducts?.filter((product) => product.choose === true) || [];
  }, [chooseProducts]);

  const handleViewOriginal = async () => {
    if (!sourceOrderId) {
      message.warning("無法獲取訂單資料");
      return;
    }

    try {
      setPreviewing(true);
      const result = await downloadOrderFormPdf({
        formType: "FNA",
        orderId: sourceOrderId,
      });

      // 创建 Blob URL 用于预览
      const blobUrl = URL.createObjectURL(result.blob);

      // 在新窗口中打开 PDF 预览
      const previewWindow = window.open(blobUrl, "_blank");

      if (!previewWindow) {
        message.warning("請允許彈出窗口以預覽檔案");
        URL.revokeObjectURL(blobUrl);
        return;
      }

      // 当窗口关闭时释放 URL
      previewWindow.addEventListener("beforeunload", () => {
        URL.revokeObjectURL(blobUrl);
      });

      message.success("正在打開預覽窗口");
    } catch (error) {
      console.error("預覽FNA文件失敗:", error);
      message.error("檔案預覽失敗，請稍後重試");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* 标题栏 */}
      <div className={styles.header}>
        <h2 className={styles.title}>最近一份財務需要分析目標概覽</h2>
        <CustomButton
          icon={<FileTextOutlined />}
          onClick={handleViewOriginal}
          loading={previewing}
          disabled={!sourceOrderId}
          className={styles.viewOriginalBtn}
          bgColor="#fff"
          color="#666"
          style={{ border: "1px solid #d9d9d9" }}
        >
          查看原始檔案
        </CustomButton>
      </div>

      <div style={{ padding: "24px" }}>
        {/* 选购产品的目标 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileAddOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>選購產品的目標</span>
          </div>
          <div className={styles.targetTags}>
            {targetTags.length > 0 ? (
              targetTags.map((tag) => (
                <div key={tag.label} className={styles.tag}>
                  <span className={styles.tagLabel}>{tag.label}</span>
                  <span className={styles.tagValue}>{tag.value}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "#94A3B8" }}>暫無數據</div>
            )}
          </div>
        </section>

        {/* 介绍的产品 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileSearchOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>介紹的產品</span>
          </div>
          <div className={styles.productList}>
            {introducedProducts.length > 0 ? (
              introducedProducts.map((product, index) => (
                <ProductCard
                  key={product.productCode || index}
                  index={index + 1}
                  name={product.productName || product.productCode || "未知产品"}
                  company={product.companyName || "待完善"}
                  paymentPeriod={paymentPeriod}
                  coveragePeriod={coveragePeriod}
                />
              ))
            ) : (
              <div style={{ color: "#94A3B8", padding: "16px 0" }}>暫無數據</div>
            )}
          </div>
        </section>

        {/* 选择的产品 */}
        <section className={styles.section}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <FileDoneOutlined />
            </span>
            <span className={styles.sectionTitleLabel}>選擇的產品</span>
          </div>
          <div className={styles.productList}>
            {selectedProducts.length > 0 ? (
              selectedProducts.map((product, index) => (
                <SelectedProductCard
                  key={product.productCode || index}
                  index={index + 1}
                  name={product.productName || product.productCode || "未知产品"}
                  company={product.companyName || "待完善"}
                  paymentPeriod={paymentPeriod}
                  coveragePeriod={coveragePeriod}
                />
              ))
            ) : (
              <div style={{ color: "#94A3B8", padding: "16px 0" }}>暫無數據</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// 产品卡片组件（介绍的产品）
function ProductCard({
  index,
  name,
  company,
  paymentPeriod,
  coveragePeriod,
}: {
  index: number;
  name: string;
  company: string;
  paymentPeriod: string;
  coveragePeriod: string;
}) {
  return (
    <div className={styles.productCard}>
      <div className={styles.productIndex}>{index}</div>
      <div className={styles.productInfo}>
        <div className={styles.productName}>{name}</div>
        <div className={styles.productDetails}>
          <span className={styles.companyName}>{company}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>繳費期：{paymentPeriod}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>保障期：{coveragePeriod}</span>
        </div>
      </div>
    </div>
  );
}

// 产品卡片组件（选择的产品）
function SelectedProductCard({
  index,
  name,
  company,
  paymentPeriod,
  coveragePeriod,
}: {
  index: number;
  name: string;
  company: string;
  paymentPeriod: string;
  coveragePeriod: string;
}) {
  return (
    <div className={styles.selectedProductCard}>
      <div className={styles.selectedProductIndex}>{index}</div>
      <div className={styles.productInfo}>
        <div className={styles.productName}>{name}</div>
        <div className={styles.productDetails}>
          <span className={styles.companyName}>{company}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>繳費期：{paymentPeriod}</span>
          <div className={styles.line}></div>
          <span className={styles.productMeta}>保障期：{coveragePeriod}</span>
        </div>
      </div>
      <CheckCircleFilled className={styles.checkIcon} />
    </div>
  );
}
